import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, LifeBuoy, Loader2, Send, MapPin, Calendar, Inbox, Sparkles, UserRound,
  Mic, Square, Volume2, VolumeX,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateMonthDay } from '../../utils/timeFormat';
import { AttachmentComposer, AttachmentTray, AttachmentList } from '../../components/messaging/MessageAttachments';

/**
 * Iter-417 — Talent Support inbox.
 *
 * The talent-facing home for the iEvntür Ops (onsite support) conversation.
 * Fixes the previously one-way flow: admin replies now surface here and the
 * talent can read + reply. Reachable from the inbox card ("iEvntür Ops
 * replied" → /talent/support) and from the Support quick action.
 */
const TalentSupport = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(params.get('thread') || null);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/messages/onsite/threads');
      setThreads(r.data?.threads || []);
    } catch (e) {
      toast.error('Failed to load support conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const [opening, setOpening] = useState(false);
  const messageAdmin = async () => {
    if (opening) return;
    setOpening(true);
    try {
      const r = await api.post('/messages/onsite/open-admin');
      setActiveId(r.data.thread_id);
    } catch (e) {
      toast.error('Could not open Admin conversation');
    } finally {
      setOpening(false);
    }
  };

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // 2026-06 — Help entry points deep-link straight into the conversation
  // (?chat=1) so mobile users never have to find a second button.
  const autoOpened = React.useRef(false);
  useEffect(() => {
    if (autoOpened.current || activeId) return;
    if (params.get('chat') !== '1') return;
    autoOpened.current = true;
    messageAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, activeId]);

  if (activeId) {
    return (
      <SupportThread
        threadId={activeId}
        onBack={() => {
          if (params.get('thread')) { navigate('/talent/messages-center?tab=support'); }
          else { setActiveId(null); loadThreads(); }
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="talent-support-page">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/talent/messages-center')} data-testid="support-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-orange-600" />
              Evie · Virtual Assistant
            </h1>
            <p className="text-xs text-slate-500">Ask about payments, bookings, applications, confirmations or your account.</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-3">
        <Button
          onClick={messageAdmin}
          disabled={opening}
          className="w-full bg-orange-600 hover:bg-orange-700 text-white justify-center"
          data-testid="message-admin-btn"
        >
          {opening ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LifeBuoy className="w-4 h-4 mr-2" />}
          Chat with Evie
        </Button>
        {loading ? (
          <div className="text-center py-16 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
          </div>
        ) : threads.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-slate-500" data-testid="support-empty-state">
              <Inbox className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <div className="font-medium">No support conversations yet</div>
              <div className="text-xs mt-1">
                Tap “Chat with Evie” above to get instant answers about your payments, bookings and account. Replies appear here.
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="support-threads-container">
            {threads.map((t) => (
              <Card
                key={t.thread_id}
                className={`hover:shadow-md cursor-pointer transition-shadow border-l-4 ${t.unread_count > 0 ? 'border-l-orange-500 bg-white' : 'border-l-slate-200 bg-slate-50/60'}`}
                onClick={() => setActiveId(t.thread_id)}
                data-testid={`support-thread-${t.thread_id}`}
              >
                <CardContent className="p-3 flex items-start gap-3">
                  <div className="shrink-0 p-2 rounded-full bg-orange-50 text-orange-700">
                    <LifeBuoy className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        Evie · Virtual Assistant
                      </span>
                      {t.unread_count > 0 && (
                        <Badge className="bg-orange-600 text-white border-0 text-[10px]" data-testid={`support-unread-${t.thread_id}`}>
                          {t.unread_count} new
                        </Badge>
                      )}
                    </div>
                    {t.last_message && (
                      <p className="text-xs text-slate-600 line-clamp-1 mt-0.5">
                        {t.last_from_ops ? 'iEvntür Support: ' : 'You: '}{t.last_message}
                      </p>
                    )}
                    <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                      {t.venue_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.venue_name}</span>}
                      {t.shift_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDateMonthDay(t.shift_date)}</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SupportThread = ({ threadId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [pending, setPending] = useState([]);
  const bottomRef = useRef(null);

  // ── Speak-to-Evie (mobile/touch) + read-aloud (device voice) ──
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const isTouch = typeof window !== 'undefined'
    && ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

  useEffect(() => () => {
    try { mediaRecorderRef.current?.stop(); } catch (_) { /* noop */ }
    try { window.speechSynthesis?.cancel(); } catch (_) { /* noop */ }
  }, []);

  const transcribeBlob = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'speech.webm');
      const r = await api.post('/support/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const text = (r.data?.text || '').trim();
      if (text) setDraft((d) => (d ? d.trim() + ' ' : '') + text);
      else toast.info("I didn't catch that — please try again or type it");
    } catch (e) {
      toast.error('Could not transcribe your audio — you can type instead');
    } finally { setTranscribing(false); }
  };

  const startRecording = async () => {
    if (recording || transcribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (blob.size) await transcribeBlob(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error('Microphone access is needed to speak to Evie');
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch (_) { /* noop */ }
    setRecording(false);
  };

  // Read-aloud uses the device's built-in voice (free, instant, offline). This
  // single function is the seam to later swap in OpenAI natural TTS without
  // changing any UI — fetch an audio URL from a backend TTS endpoint and play it.
  const SPEECH_RATE = 0.9; // slightly slower than default so Evie sounds natural (drop to 0.85 if still rushed)
  // Permanent spoken-pronunciation rule: the brand renders as "iEvntür" on screen
  // but must be READ ALOUD as "i even ture". Applies to every read-aloud response.
  const toSpeakable = (text) => (text || '').replace(/iEvnt(?:ü|u)r/gi, 'i even ture');
  const toggleSpeak = (id, text) => {
    try {
      const synth = window.speechSynthesis;
      if (!synth) { toast.info('Read-aloud is not supported on this device'); return; }
      if (speakingId === id) { synth.cancel(); setSpeakingId(null); return; }
      synth.cancel();
      const u = new SpeechSynthesisUtterance(toSpeakable(text));
      u.rate = SPEECH_RATE;
      u.onend = () => setSpeakingId(null);
      u.onerror = () => setSpeakingId(null);
      setSpeakingId(id);
      synth.speak(u);
    } catch (_) { setSpeakingId(null); }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/messages/onsite/thread/${threadId}`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to open conversation');
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [data?.messages?.length]);

  const send = async () => {
    const content = draft.trim();
    if ((!content && pending.length === 0) || sending) return;
    setSending(true);
    try {
      await api.post('/messages/onsite/send', {
        thread_id: threadId,
        content,
        booking_id: data?.thread?.booking_id,
        shift_id: data?.thread?.shift_id,
        attachments: pending,
        client_request_id: (window.crypto?.randomUUID?.() || `sup-${Date.now()}`),
      });
      setDraft('');
      setPending([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const talkToHuman = async () => {
    if (escalating) return;
    setEscalating(true);
    try {
      await api.post(`/messages/onsite/thread/${threadId}/escalate`, {});
      toast.success('Connecting you with a human iEvntür team member');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not escalate');
    } finally {
      setEscalating(false);
    }
  };

  const bubbleClass = (kind, isOwn) => {
    if (isOwn) return 'bg-orange-600 text-white rounded-br-sm';
    if (kind === 'ai') return 'bg-indigo-50 border border-indigo-200 text-indigo-950 rounded-bl-sm';
    return 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm';
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="support-thread-view">
      <header className="bg-orange-50 border-b border-orange-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="support-thread-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-orange-900 flex items-center gap-2">
              <LifeBuoy className="w-4 h-4" /> Evie · Virtual Assistant
            </h1>
            <p className="text-[11px] text-orange-800/80">Instant answers from your real account details</p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={talkToHuman}
            disabled={escalating}
            className="border-orange-300 text-orange-800 bg-white hover:bg-orange-100 shrink-0"
            data-testid="support-talk-to-human-btn"
          >
            {escalating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <UserRound className="w-3.5 h-3.5 mr-1" />}
            Request review
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto" data-testid="support-thread-messages">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-2">
          <div className="flex items-start gap-2 text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2" data-testid="support-ai-banner">
            <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Evie answers instantly using your real bookings, payments and account details. Anything that needs a closer look is submitted for review by the iEvntür team right here.</span>
          </div>
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-orange-600 inline" /></div>
          ) : (data?.messages || []).length === 0 ? (
            <div className="flex justify-start" data-testid="support-thread-empty">
              <div className="max-w-[80%] rounded-2xl px-3 py-2 text-sm bg-indigo-50 text-indigo-900 border border-indigo-100">
                <div className="flex items-center gap-1 text-[10px] font-semibold text-indigo-700 mb-1">
                  <Sparkles className="w-3 h-3" /> Evie · Virtual Assistant
                </div>
                <div className="whitespace-pre-wrap">Hi, I&apos;m Evie with iEvntür Support. I can help with your bookings, payments, applications, account, and other iEvntür questions. What can I help you with today?</div>
              </div>
            </div>
          ) : (
            (data.messages || []).map((m) => {
              const kind = m.sender_kind || (m.is_own ? 'talent' : 'admin');
              return (
                <div key={m.id} className={`flex ${m.is_own ? 'justify-end' : 'justify-start'}`} data-testid={`support-msg-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${bubbleClass(kind, m.is_own)}`}>
                    {kind === 'ai' && (
                      <div className="flex items-center gap-1 text-[10px] font-semibold text-indigo-700 mb-1" data-testid={`support-msg-ai-label-${m.id}`}>
                        <Sparkles className="w-3 h-3" /> Evie · Virtual Assistant
                      </div>
                    )}
                    {m.content && <div className="whitespace-pre-wrap break-words">{m.content}</div>}
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mt-1.5"><AttachmentList attachments={m.attachments} align={m.is_own ? 'right' : 'left'} /></div>
                    )}
                    <div className={`text-[10px] mt-1 ${m.is_own ? 'text-orange-100' : (kind === 'ai' ? 'text-indigo-500' : 'text-slate-500')}`}>
                      {m.is_own ? 'You' : (kind === 'ai' ? 'Evie · Virtual Assistant' : 'iEvntür Support Team')}
                      {m.created_at ? ` · ${new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                    </div>
                    {kind === 'ai' && m.content && (
                      <button
                        type="button"
                        onClick={() => toggleSpeak(m.id, m.content)}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 hover:text-indigo-800"
                        data-testid={`support-readaloud-${m.id}`}
                        aria-label={speakingId === m.id ? 'Stop reading aloud' : 'Read this reply aloud'}
                      >
                        {speakingId === m.id ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                        {speakingId === m.id ? 'Stop' : 'Listen'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <footer className="bg-white border-t sticky bottom-0">
        <div className="max-w-3xl mx-auto px-3 py-3">
          <AttachmentTray pending={pending} setPending={setPending} />
          <div className="flex items-end gap-2">
            <AttachmentComposer pending={pending} setPending={setPending} disabled={sending} />
            {isTouch && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={recording ? stopRecording : startRecording}
                disabled={sending || transcribing}
                className={`shrink-0 ${recording ? 'bg-red-50 border-red-300 text-red-600 animate-pulse' : ''}`}
                data-testid="support-mic-btn"
                aria-label={recording ? 'Stop recording' : 'Speak to Evie'}
              >
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
            )}
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={recording ? 'Listening… tap ■ to stop' : 'Ask Evie anything…'}
              rows={1}
              className="min-h-[40px] resize-none"
              data-testid="support-reply-input"
            />
            <Button
              onClick={send}
              disabled={sending || (!draft.trim() && pending.length === 0)}
              className="bg-orange-600 hover:bg-orange-700 text-white shrink-0"
              data-testid="support-reply-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TalentSupport;
